import { Box, Text, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import React from 'react';
import CustomButton from '@/components/atoms/CustomButton';

type Props = {
  icon: LucideIcon;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
};

// A friendly empty / error placeholder: an icon in a soft halo, a heading + subtext,
// and an optional contextual action (e.g. "Clear filters" / "Try again").
const ContentEmptyState = ({ icon: Icon, title, description, actionLabel, onAction }: Props) => (
  <VStack width="100%" paddingY="60px" gap="18px" textAlign="center">
    <motion.div
      initial={{ scale: 0.7, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
    >
      <Box
        width="100px"
        height="100px"
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgGradient="linear(135deg, #EDE8FD 0%, #F8F5FF 100%)"
        color="primary.500"
        boxShadow="0px 10px 30px rgba(78, 26, 239, 0.14)"
      >
        <Icon size={44} strokeWidth={1.8} />
      </Box>
    </motion.div>

    <VStack gap="6px" maxWidth="440px">
      <Text fontSize="20px" fontWeight={800} color="text.darkBlue">
        {title}
      </Text>
      <Text fontSize="14px" color="text.mediumBlue" lineHeight="20px">
        {description}
      </Text>
    </VStack>

    {actionLabel && onAction && (
      <CustomButton label={actionLabel} backgroundColor="primary.500" labelColor="extra.white" width="220px" onClick={onAction} />
    )}
  </VStack>
);

export default ContentEmptyState;
